import { clampPaneRatio } from '@hooks/usePageLayout/pageLayout';

/**
 * Pure rules of the split view (RD-2957 Phase 4): which row the arrow keys open, and
 * where a dragged pane divider leaves the list pane.
 */

export type ActiveStep =
  /** Open the row at this index of the current page. */
  | { kind: 'row'; index: number }
  /** Go to another page and open its first or last row once it has loaded. */
  | { kind: 'page'; page: number; edge: 'first' | 'last' }
  /** Nothing further in that direction. */
  | { kind: 'none' };

/**
 * One step up (-1) or down (+1) from the active row. Past the end of a page the next page opens on
 * its first row, before the start the previous page opens on its last — so ↓ walks through the
 * whole result, not just one page of it.
 */
export const stepActive = (args: { index: number; delta: -1 | 1; rowCount: number; page: number; totalPages: number }): ActiveStep => {
  const { index, delta, rowCount, page, totalPages } = args;
  if (rowCount === 0) return { kind: 'none' };
  // No active row yet (e.g. the user starts with an arrow key): start at the edge they point to.
  if (index < 0) return { kind: 'row', index: delta === 1 ? 0 : rowCount - 1 };
  const next = index + delta;
  if (next >= 0 && next < rowCount) return { kind: 'row', index: next };
  if (next >= rowCount && page < totalPages) return { kind: 'page', page: page + 1, edge: 'first' };
  if (next < 0 && page > 1) return { kind: 'page', page: page - 1, edge: 'last' };
  return { kind: 'none' };
};

/**
 * The list pane's share in % while dragging the divider: where the pointer is within the container,
 * kept so both panes stay usable.
 */
export const ratioFromPointer = (containerStart: number, containerSize: number, pointer: number, fallback: number): number =>
  containerSize > 0 ? clampPaneRatio(((pointer - containerStart) / containerSize) * 100, fallback) : fallback;
